import { type FileService } from "@/shared/domain/files";
import {
  type MessagePathRepresentationMapping,
  type RepresentationSelector,
  type TopicService,
  RepresentationStorageFormat,
  resolveMessagePathFiles,
} from "@/shared/domain/topics";
import { SharedTask } from "@/shared/synchronization";

import { McapTopicReader } from "./McapTopicReader";
import { ParquetMetadataReader } from "./parquet/ParquetMetadataReader";
import { ParquetTopicReader } from "./ParquetTopicReader";
import { type TopicReader } from "./TopicReader";

/** The topic data a panel asks to read. */
export interface TopicReaderRequest {
  topicId: string;
  /**
   * The message path the reader is for. An opened reader can read every
   * message path of the topic, but a {@link ReaderRegistry} keeps one reader
   * per message path, so that each field's reader, with the data it has
   * downloaded, is disposed once no consumer reads that field.
   */
  messagePathId?: string;
  representationSelector?: RepresentationSelector;
  signal?: AbortSignal;
}

/** Opens a new reader of a topic's stored data. */
export type OpenTopicReader = (
  request: TopicReaderRequest,
) => Promise<TopicReader>;

/**
 * Opens readers of the representation each request selects, as MCAP or
 * Parquet readers by the representation's storage format. The opener fetches
 * each topic's representations once for the requests that open readers of it
 * at the same time, and keeps them once a fetch succeeds; a failed fetch is
 * tried again by the next request. Its Parquet readers share one cache of
 * file footers.
 */
export function topicReaderOpener({
  fileService,
  topicService,
}: {
  fileService: FileService;
  topicService: TopicService;
}): OpenTopicReader {
  // Topic representations are append-only and workers are short-lived, so
  // the fetched representations are never refreshed.
  const mappingsFetchByTopic = new Map<
    string,
    SharedTask<MessagePathRepresentationMapping[]>
  >();
  const parquetMetadataReader = new ParquetMetadataReader();

  function mappingsFor(
    topicId: string,
    signal?: AbortSignal,
  ): Promise<MessagePathRepresentationMapping[]> {
    let mappingsFetch = mappingsFetchByTopic.get(topicId);
    if (mappingsFetch === undefined) {
      const created = new SharedTask((fetchSignal) =>
        topicService.getMessagePathRepresentations(topicId, {
          abortSignal: fetchSignal,
        }),
      );
      mappingsFetchByTopic.set(topicId, created);
      // This wait has no signal, so the fetch runs to the end even when every
      // request waiting on it gives up, and its result is kept for the next.
      void created.wait().catch(() => {
        if (mappingsFetchByTopic.get(topicId) === created) {
          mappingsFetchByTopic.delete(topicId);
        }
      });
      mappingsFetch = created;
    }
    return mappingsFetch.wait(signal);
  }

  return async ({ topicId, representationSelector, signal }) => {
    const allMappings = await mappingsFor(topicId, signal);
    const resolved = resolveMessagePathFiles(
      allMappings.filter((m) => m.representation !== undefined),
      representationSelector,
    );
    if (resolved.length === 0) {
      const cause = representationSelector
        ? `No representation matches the requested selector for topic ${topicId}`
        : `No representations found for topic ${topicId}`;
      throw new Error(
        "Encountered an error reading topic data. Please reach out to Roboto support.",
        { cause },
      );
    }

    // All resolved mappings share the same storage format (selectRepresentation
    // is applied per message path, but the API groups by format at the topic level).
    const storageFormat = resolved[0].representation.storage_format;
    switch (storageFormat) {
      case RepresentationStorageFormat.MCAP: {
        return new McapTopicReader(topicId, resolved, fileService);
      }
      case RepresentationStorageFormat.PARQUET: {
        return new ParquetTopicReader(
          topicId,
          resolved,
          fileService,
          parquetMetadataReader,
        );
      }
      default: {
        throw new Error(
          `Support for reading data stored as ${String(storageFormat)} not yet implemented.`,
        );
      }
    }
  };
}
